// src/pages/FAQ.jsx
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { getActiveRole } from '../utils/auth';
import { faqData } from '../config/faqData';
import { regionalizeFaqItems, localizeFaqItem } from '../config/faqRegional';
import { useRegionalSettings } from '../context/RegionalSettingsContext';
import PageHeaderSection from '../components/layout/PageHeaderSection';

const roleColors = {
    ADMIN: { pill: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
    MANAGER: { pill: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
    COACH: { pill: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-500' },
    STUDENT: { pill: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
    PARENT: { pill: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
};

function resolveLang() {
    const raw = (i18n.language || 'en').slice(0, 2);
    return ['de', 'fr', 'nl'].includes(raw) ? raw : 'en';
}

function AccordionItem({ q, a, isOpen, onToggle }) {
    return (
        <div
            className={`rounded-xl border transition-all duration-200 ${
                isOpen
                    ? 'border-primary-300 bg-primary-50/40 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300'
            }`}
        >
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-start justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={isOpen}
            >
                <span className={`text-sm font-semibold leading-snug ${isOpen ? 'text-primary-800' : 'text-slate-800'}`}>
                    {q}
                </span>
                <span
                    className={`mt-0.5 h-5 w-5 shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-45 text-primary-600' : 'text-slate-400'
                    }`}
                >
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                </span>
            </button>
            {isOpen && (
                <div className="px-5 pb-4">
                    <div className="border-t border-primary-100 pt-3">
                        <p className="text-sm leading-relaxed text-slate-600">{a}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function FAQ() {
    const { t, i18n: i18nInstance } = useTranslation();
    const lang = resolveLang();
    const role = getActiveRole();
    const { settings } = useRegionalSettings();
    const items = useMemo(() => {
        const regional = regionalizeFaqItems(faqData[role] || [], settings?.countryCode);
        return regional.map((item) => localizeFaqItem(item, lang));
    }, [role, settings?.countryCode, lang, i18nInstance.language]);
    const colors = roleColors[role] || roleColors.ADMIN;

    const [openKey, setOpenKey] = useState(null);
    const [search, setSearch] = useState('');
    const [activeSection, setActiveSection] = useState(null); // null = all sections

    const allLabel = t('faq.allSections');

    const sections = useMemo(() => {
        return [allLabel, ...new Set(items.map((i) => i.sectionText))];
    }, [items, allLabel]);

    const filtered = useMemo(() => {
        let list = items;
        if (activeSection != null && activeSection !== allLabel) {
            list = list.filter((i) => i.sectionText === activeSection);
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(
                (i) => i.qText.toLowerCase().includes(q) || i.aText.toLowerCase().includes(q),
            );
        }
        return list;
    }, [items, activeSection, search, allLabel]);

    const grouped = useMemo(() => {
        const map = {};
        filtered.forEach((item) => {
            if (!map[item.sectionText]) map[item.sectionText] = [];
            map[item.sectionText].push(item);
        });
        return map;
    }, [filtered]);

    const toggle = (key) => setOpenKey((prev) => (prev === key ? null : key));

    return (
        <div className="list-page">
            <PageHeaderSection
                eyebrow={t('nav.help')}
                title={t('faq.title')}
                subtitle={t('faq.subtitle', {
                    count: items.length,
                    role: t(`faq.roles.${role}`, { defaultValue: role }),
                })}
                aside={(
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${colors.pill}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
                        {t(`faq.roles.${role}`, { defaultValue: role })}
                    </span>
                )}
            />

            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <div className="border-b border-slate-100 bg-slate-50/80 px-5 py-4 sm:px-6">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">{t('faq.findAnswers')}</p>
                    <h2 className="mt-1 text-lg font-semibold text-slate-900">{t('faq.searchHeading')}</h2>
                    <p className="mt-1 text-sm text-slate-600">{t('faq.searchHint')}</p>
                </div>
                <div className="space-y-4 px-5 py-5 sm:px-6">
                    <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center text-slate-400">
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                    d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                            </svg>
                        </span>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setOpenKey(null); }}
                            placeholder={t('faq.searchPlaceholder')}
                            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-400"
                        />
                        {search && (
                            <button
                                type="button"
                                onClick={() => setSearch('')}
                                className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600"
                            >
                                <svg className="h-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {sections.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => { setActiveSection(s); setOpenKey(null); }}
                                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                    (activeSection == null && s === allLabel) || activeSection === s
                                        ? 'border-primary-600 bg-primary-600 text-white'
                                        : 'border-slate-200 bg-white text-slate-600 hover:border-primary-300 hover:bg-primary-50'
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>

                    {search && (
                        <p className="text-xs text-slate-500">
                            {filtered.length === 0
                                ? t('faq.noQuestionsMatch')
                                : t('faq.resultsFor', { count: filtered.length, query: search })}
                        </p>
                    )}
                </div>
            </section>

            {filtered.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center text-slate-400 shadow-sm">
                    <p className="text-sm font-medium">{t('faq.noResults')}</p>
                    <p className="mt-1 text-xs">{t('faq.tryDifferent')}</p>
                </div>
            ) : (
                <div className="space-y-6">
                    {Object.entries(grouped).map(([section, sectionItems]) => (
                        <section key={section} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/80 px-5 py-3 sm:px-6">
                                <h2 className="text-sm font-semibold text-slate-900">{section}</h2>
                                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
                                    {sectionItems.length}
                                </span>
                            </div>
                            <div className="space-y-2 px-5 py-4 sm:px-6">
                                {sectionItems.map((item) => (
                                    <AccordionItem
                                        key={item.id}
                                        q={item.qText}
                                        a={item.aText}
                                        isOpen={openKey === item.id}
                                        onToggle={() => toggle(item.id)}
                                    />
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-slate-50 px-6 py-5 text-center">
                <p className="text-sm text-slate-500">
                    {t('faq.footerPrompt')}{' '}
                    <span className="font-medium text-slate-700">{t('faq.footerAction')}</span>
                </p>
            </div>
        </div>
    );
}
